<template>
  <div class="onboarding-step franchises-step">
    <div class="step-header">
      <h3>{{ t('Franquias') }}</h3>
      <p class="step-description">{{ t('Selecione as franquias que o usuário terá acesso') }}</p>
    </div>

    <div v-if="loading" class="loading-state">
      <spinner style="width: 50px; height: 50px" />
      <p>{{ t('Carregando franquias...') }}</p>
    </div>

    <div v-else-if="availableFranchises.length === 0" class="empty-state">
      <i class="pi pi-building" style="font-size: 3rem; color: #d1d5db;"></i>
      <p>{{ t('Nenhuma franquia disponível') }}</p>
    </div>

    <div v-else>
      <!-- Search and controls -->
      <div class="franchises-controls">
        <div class="search-box">
          <span class="p-input-icon-left" style="width: 100%;">
            <i class="pi pi-search" />
            <input-text
              v-model="searchQuery"
              elem-id="franchise-search"
              :placeholder="t('Buscar franquia...')"
              data-testid="users-onboarding-franchise-search"
              style="width: 100%;"
            />
          </span>
        </div>

        <div class="control-buttons">
          <Button
            :label="t('Selecionar Todas')"
            icon="pi pi-check-square"
            size="small"
            outlined
            data-testid="users-onboarding-franchise-select-all"
            @click="selectAll"
          />
          <Button
            :label="t('Limpar Seleção')"
            icon="pi pi-times"
            size="small"
            outlined
            severity="secondary"
            data-testid="users-onboarding-franchise-clear"
            @click="clearAll"
          />
        </div>
      </div>

      <!-- Selected count -->
      <div v-if="selectedFranchises.length > 0" class="selected-summary">
        <i class="pi pi-check-circle"></i>
        <span>
          <strong>{{ selectedFranchises.length }}</strong>
          {{ selectedFranchises.length === 1 ? t('franquia selecionada') : t('franquias selecionadas') }}
        </span>
      </div>

      <!-- Franchises list -->
      <div class="franchises-list">
        <div
          v-for="franchise in filteredFranchises"
          :key="franchise.id"
          class="franchise-item"
          :class="{ selected: isFranchiseSelected(franchise.id) }"
        >
          <div class="franchise-item-content">
            <div class="franchise-info">
              <div class="franchise-header">
                <i class="pi pi-building franchise-icon"></i>
                <h4 class="franchise-name">{{ franchise.name }}</h4>
                <Badge
                  v-if="franchise.status === 'active'"
                  value="Ativa"
                  severity="success"
                  class="franchise-badge"
                />
                <Badge
                  v-else-if="franchise.status === 'inactive'"
                  value="Inativa"
                  severity="danger"
                  class="franchise-badge"
                />
              </div>
              <p v-if="franchise.location" class="franchise-location">
                <i class="pi pi-map-marker"></i>
                {{ franchise.location }}
              </p>
            </div>
            <div class="franchise-toggle">
              <input-toggle
                :elem-id="`franchise-${franchise.id}`"
                :model-value="isFranchiseSelected(franchise.id)"
                :label="''"
                :data-testid="`users-onboarding-franchise-${franchise.id}`"
                @update:model-value="(value: boolean) => toggleFranchise(franchise.id, value)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- No results -->
      <div v-if="filteredFranchises.length === 0" class="no-results">
        <i class="pi pi-search" style="font-size: 2rem; color: #d1d5db;"></i>
        <p>{{ t('Nenhuma franquia encontrada para "{query}"', { query: searchQuery }) }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';

interface AvailableFranchise {
  id: string;
  name: string;
  location: string;
  status: string;
}

const { t } = useI18n();

// Props
const props = withDefaults(defineProps<{
  modelValue?: string[];
  availableFranchises?: AvailableFranchise[];
  loading?: boolean;
}>(), {
  modelValue: () => [],
  availableFranchises: () => [],
  loading: false
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string[]];
}>();

// Local state
const searchQuery = ref<string>('');

const selectedFranchises = computed<string[]>({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Filter franchises by search
const filteredFranchises = computed<AvailableFranchise[]>(() => {
  if (!searchQuery.value) {
    return props.availableFranchises;
  }

  const query = searchQuery.value.toLowerCase();
  return props.availableFranchises.filter(franchise => {
    return (
      franchise.name?.toLowerCase().includes(query) ||
      franchise.location?.toLowerCase().includes(query)
    );
  });
});

// Methods
const isFranchiseSelected = (franchiseId: string): boolean => {
  return selectedFranchises.value.includes(franchiseId);
};

const toggleFranchise = (franchiseId: string, value: boolean): void => {
  if (value) {
    // Add franchise
    if (!selectedFranchises.value.includes(franchiseId)) {
      selectedFranchises.value = [...selectedFranchises.value, franchiseId];
    }
  } else {
    // Remove franchise
    const index = selectedFranchises.value.indexOf(franchiseId);
    if (index > -1) {
      const newSelection = [...selectedFranchises.value];
      newSelection.splice(index, 1);
      selectedFranchises.value = newSelection;
    }
  }

  log('OnboardingStep2Franchises', 'toggleFranchise', 'Franchise toggled', {
    franchiseId,
    value,
    selected: selectedFranchises.value
  });
};

const selectAll = (): void => {
  selectedFranchises.value = filteredFranchises.value.map(f => f.id);
  log('OnboardingStep2Franchises', 'selectAll', 'All franchises selected');
};

const clearAll = (): void => {
  selectedFranchises.value = [];
  log('OnboardingStep2Franchises', 'clearAll', 'Selection cleared');
};
</script>

<style scoped>
.onboarding-step {
  padding: 20px 0;
}

.step-header {
  margin-bottom: 24px;
}

.step-header h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin: 0;
  color: #666;
  font-size: 0.875rem;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.loading-state p,
.empty-state p {
  color: #666;
  font-size: 0.875rem;
}

.franchises-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 200px;
}

.control-buttons {
  display: flex;
  gap: 8px;
}

.selected-summary {
  padding: 12px 16px;
  background: #f0f9ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  border: 1px solid #bae6fd;
}

.selected-summary i {
  color: #0ea5e9;
  font-size: 1.25rem;
}

.selected-summary span {
  color: #333;
  font-size: 0.875rem;
}

.selected-summary strong {
  color: #0ea5e9;
  font-weight: 600;
}

.franchises-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}

.franchise-item {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.franchise-item:hover {
  border-color: #6366f1;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.1);
}

.franchise-item.selected {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.franchise-item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.franchise-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.franchise-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.franchise-icon {
  font-size: 1.5rem;
  color: #6366f1;
  flex-shrink: 0;
}

.franchise-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.franchise-location {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  line-height: 1.4;
  padding-left: 44px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.franchise-location i {
  font-size: 0.75rem;
  color: #999;
  flex-shrink: 0;
}

.franchise-badge {
  margin-left: auto;
}

.franchise-toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.franchise-toggle :deep(.toggle-container) {
  margin: 0;
  gap: 0;
}

.franchise-toggle :deep(.toggle-label) {
  display: none;
}

.no-results {
  padding: 40px 20px;
  text-align: center;
  color: #666;
}

.no-results p {
  margin-top: 12px;
  font-size: 0.875rem;
}

/* Scrollbar styling */
.franchises-list::-webkit-scrollbar {
  width: 8px;
}

.franchises-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.franchises-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.franchises-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Responsive */
@media (max-width: 768px) {
  .franchises-controls {
    flex-direction: column;
  }

  .search-box,
  .control-buttons {
    width: 100%;
  }

  .control-buttons {
    flex-direction: column;
  }

  .control-buttons :deep(.p-button) {
    width: 100%;
  }

  .franchise-item-content {
    gap: 12px;
  }

  .franchise-header {
    flex-wrap: wrap;
  }

  .franchise-location {
    padding-left: 0;
  }
}
</style>
